<script>

import DeleteWithConfirmation from "@/components/DeleteWithConfirmation.vue";
import FiltersPanel from "@/components/filters/FiltersPanel.vue";
import {useVisibility} from "@/composables/useVisibility.js";
import PerPageSelect from "@/components/ui/PerPageSelect.vue";
import UserCompanies from "@/components/users/UserCompanies.vue";

export default {
    components: {UserCompanies, FiltersPanel, DeleteWithConfirmation, PerPageSelect},
    setup() {
        const { showTax, showAutoSettlement } = useVisibility()

        return {
            showTax,
            showAutoSettlement
        }
    },
    data() {
        return {
            error: '',
            loading: true,
            downloading: false,
            users: [],
            meta: {
                current_page: 1,
                total: 0,
                per_page: 0,
            },
            sort: {
                field: 'last_name',
                order: 'asc'
            },
            controller: null,
            filters: {},
            panelFilters: {},
            tableFilters: {},
            perPage: parseInt(localStorage.getItem('perPage_employees') || '10'),
        }
    },
    computed: {
        filtersDef() {
            return [
                { key: 'first_name', label: this.$t('users.fields.first_name.name'), type: 'text' },
                { key: 'last_name', label: this.$t('users.fields.last_name.name'), type: 'text' },
                { key: 'company.name', label: this.$t('users.fields.company.name'), type: 'text' },
                { key: 'pesel', label: this.$t('users.fields.pesel.name'), type: 'text' },
                { key: 'phone_number', label: this.$t('users.fields.phone_number.name'), type: 'text' },
                { key: 'email', label: this.$t('users.fields.email.name'), type: 'text' },
                { key: 'bank_account_number', label: this.$t('users.fields.bank_account_number.name'), type: 'text' },
                {
                    key: 'inactive_with_tokens',
                    label: this.$t('users.labels.inactive_accounts_filter'),
                    type: 'text',
                    placeholder: '30',
                    tooltip: this.$t('users.labels.inactive_accounts_filter_info')
                },
                {
                    key: 'active_with_tokens',
                    label: this.$t('users.labels.active_accounts_filter'),
                    type: 'text',
                    placeholder: '30',
                    tooltip: this.$t('users.labels.active_accounts_filter_info')
                },
                {
                    key: 'no_trainings_tokens',
                    label: this.$t('users.labels.no_trainings_tokens_filter'),
                    type: 'text',
                    placeholder: '30' ,
                    tooltip: this.$t('users.labels.no_trainings_tokens_filter_info'),
                },
                {
                    key: 'trainings_tokens',
                    label: this.$t('users.labels.trainings_tokens_filter'),
                    type: 'text',
                    placeholder: '30' ,
                    tooltip: this.$t('users.labels.trainings_tokens_filter_info'),
                },
                { key: 'token_balance', label: this.$t('users.fields.token_balance.name'), type: 'number' },
                {
                    key: 'without_company',
                    label: this.$t('users.labels.without_company_filter'),
                    type: 'checkbox',
                },
            ];
        }
    },
    methods: {
        onFiltersChange(filters) {
            this.panelFilters = filters;
            this.filters = {
                ...this.panelFilters,
                ...filters
            }
            this.meta.current_page = 1;
            this.loadData();
        },
        loadData(scrollTop = true) {
            if (this.controller) {
                this.controller.abort();
            }
            this.controller = new AbortController();
            this.loading = true;
            if (scrollTop) {
                window.scrollTo({top: 0});
            }
            return axios.get('/users/employees', {
                signal: this.controller.signal,
                params: {
                    page: this.meta.current_page,
                    per_page: this.perPage,
                    sort: this.sort.field,
                    order: this.sort.order,
                    filters: this.filters,
                }
            }).then(response => {
                this.users = response.data.data;
                this.meta.current_page = response.data.meta.current_page;
                this.meta.total = response.data.meta.total;
                this.meta.per_page = response.data.meta.per_page;
            }).catch(error => {
            }).finally(() => {
                this.loading = false;
            })
        },
        onPageChange(page) {
            this.controller.abort();
            this.meta.current_page = page;
            this.loadData();
        },
        onSort(field, order) {
            this.controller.abort();
            this.sort = {field, order};
            this.loadData();
        },
        onDelete() {
            let top = window.scrollY;
            this.loadData(false).then(() => {
                window.scrollTo({top: top});
            });
        },
        onPerPageChange() {
            localStorage.setItem('perPage_employees', String(this.perPage));
            this.meta.current_page = 1;
            this.loadData();
        },
        download() {
            this.downloading = true;
            let params = {
                sort: this.sort.field,
                order: this.sort.order,
                filters: this.filters,
                xlsx: 1
            }
            axios({
                url: '/users/employees',
                method: 'GET',
                responseType: 'blob',
                params
            }).then((response) => {
                saveAs(response.data, 'employees.xlsx');
            }).finally(() => {
                this.downloading = false;
            });
        }
    },
    mounted() {
        const params = new URLSearchParams(window.location.search);
        if (params.get('inactive_with_tokens')) {
            this.filters.inactive_with_tokens = params.get('inactive_with_tokens');
        }
        if (params.get('no_trainings_tokens')) {
            this.filters.no_trainings_tokens = params.get('no_trainings_tokens');
        }
        if (params.get('without_company')) {
            this.filters.without_company = 1;
        }
        this.loadData();
    },
    beforeUnmount() {
        this.controller.abort();
    }
}

</script>
<template>
    <div class="employees-index users-index">

        <div class="buttons" v-if="$auth.isEmployer() || $auth.isAdmin()">
            <b-button type="is-link" @click="$router.push({name: 'employees.create'})">
                {{ $t('employees.labels.add_employee') }}
            </b-button>
            <b-button type="is-link" @click="$router.push({name: 'employees.import'})">
                {{ $t('employees.labels.import') }}
            </b-button>
            <b-button v-if="$auth.isAdmin()"
                      :loading="downloading"
                      type="is-link"
                      icon-left="download"
                      @click="download">
                .xlsx
            </b-button>
        </div>
        <div class="mb-4">
            <FiltersPanel
                :filters="filtersDef"
                @update:activeFilters="onFiltersChange"
            />
        </div>
        <b-table
            :data="loading ? [] : users"
            :loading="loading"
            detailed
            :show-detail-icon="true"
            detail-icon="chevron-right"

            :paginated="!loading"
            backend-pagination
            :total="loading ? 0 : meta.total"
            :per-page="perPage"
            @page-change="onPageChange"
            :aria-next-label="$t('navigation.next_page')"
            :aria-previous-label="$t('navigation.previous_page')"
            :aria-page-label="$t('navigation.page')"
            :aria-current-label="$t('navigation.current_page')"

            backend-sorting
            sort-icon="arrow-up"
            default-sort-direction="asc"
            :default-sort="['last_name', 'asc']"
            @sort="onSort">

            <b-table-column field="first_name" :label="$t('users.fields.first_name.name')" sortable>
                <template #default="props">
                    {{ props.row.first_name }}
                </template>
                <template #searchable="props">
                    <b-input
                        v-model="props.filters[props.column.field]"
                        placeholder=""
                        icon="magnify"/>
                </template>
            </b-table-column>

            <b-table-column field="last_name" :label="$t('users.fields.last_name.name')" sortable>
                <template #default="props">
                    {{ props.row.last_name }}
                </template>
                <template #searchable="props">
                    <b-input
                        v-model="props.filters[props.column.field]"
                        placeholder=""
                        icon="magnify"/>
                </template>
            </b-table-column>

            <b-table-column field="company.name" :label="$t('users.fields.company.name')"
                            v-if="$auth.user.role === 'admin'">
                <template #default="props">

                    <div v-for="company in props.row.companies">
                        {{ company.name }}
                    </div>

                </template>
                <template #searchable="props">
                    <b-input
                        v-model="props.filters[props.column.field]"
                        placeholder=""
                        icon="magnify"/>
                </template>
            </b-table-column>

            <b-table-column field="token_balance" :label="$t('users.fields.token_balance.name')" sortable
                            v-slot="props">
                {{ props.row.token_balance }}
            </b-table-column>

            <b-table-column v-if="showAutoSettlement" field="auto_settlement" :label="$t('users.fields.auto_settlement.name')" sortable
                            v-slot="props">
                <span>
                    <b-icon :icon="props.row.auto_settlement ? 'check-bold' : 'close-thick'"/>
                </span>
            </b-table-column>

            <b-table-column v-if="showTax" field="tax_rate" :label="$t('users.fields.tax_rate.name')" sortable v-slot="props">
                {{
                    props.row.tax_rate && props.row.tax_active ? (props.row.tax_rate + '%') : '-'
                }}
            </b-table-column>

            <b-table-column label="-" v-slot="props">
                <section class="buttons">
                    <b-button type="is-link"
                              size="is-small"
                              :title="$t('actions.edit')"
                              icon-right="square-edit-outline"
                              @click="$router.push({name: 'employees.edit', params: {id: props.row.id}})">
                    </b-button>
                    <DeleteWithConfirmation
                        :url="'/users/' + props.row.id"
                        small icon
                        @deleted="onDelete"
                        :content="$t('employees.messages.employee_removal_confirmation')"/>
                </section>
            </b-table-column>

            <template #empty>
                <div v-if="!loading" class="block has-text-grey has-text-centered mb-4 mt-4">
                    {{ $t('employees.labels.no_employees') }}
                </div>
            </template>

            <template #detail="props">
                <article class="media">
                    <div class="media-content">
                        <div class="content">
                            <p>
                                <strong class="employee-name is-size-5">
                                    {{ props.row.first_name }} {{ props.row.last_name }}
                                </strong>
                            </p>
                            <div class="fixed-grid has-2-cols has-1-cols-mobile">
                                <div class="grid">
                                    <div class="cell">
                                        <p v-if="$auth.isAdmin()">
                                            <UserCompanies :user="props.row"/>

                                        </p>
                                        <p>
                                            <b-icon icon="at"/>
                                            <span class="detail-label">{{ $t('users.fields.email.name') }}:</span>
                                            {{ props.row.email }}
                                        </p>
                                        <p v-if="props.row.phone_number">
                                            <b-icon icon="phone"/>
                                            <span class="detail-label">{{ $t('users.fields.phone_number.name') }}:</span>
                                            {{ props.row.phone_number }}
                                        </p>
                                        <p v-if="props.row.pesel">
                                            <b-icon icon="account"/>
                                            <span class="detail-label">{{ $t('users.fields.pesel.name') }}:</span>
                                            {{ props.row.pesel }}
                                        </p>
                                        <p v-if="props.row.login_code">
                                            <b-icon icon="login"/>
                                            <span class="detail-label">{{ $t('auth.fields.one_time_login_code.name') }}:</span>
                                            {{ props.row.login_code }}
                                        </p>
                                    </div>
                                    <div class="cell">
                                        <p>
                                            <b-icon icon="bank"/>
                                            <span class="detail-label">{{ $t('users.fields.bank_account_number.name') }}:</span>
                                            {{ props.row.bank_account_number }}
                                        </p>
                                        <p>
                                            <b-icon icon="cash-multiple"/>
                                            <span class="detail-label">{{ $t('users.fields.token_balance.name') }}:</span>
                                            {{ props.row.token_balance }}
                                        </p>
                                        <p v-if="showAutoSettlement">
                                            <b-icon icon="calculator"/>
                                            <span class="detail-label">{{ $t('users.fields.auto_settlement.name') }}:</span>
                                            <b-icon
                                                :icon="props.row.auto_settlement ? 'check-bold' : 'close-thick'"/>
                                        </p>
                                        <p v-if="showTax">
                                            <b-icon icon="percent"/>
                                            <span class="detail-label">{{ $t('users.fields.tax_rate.name') }}: </span>
                                            <span v-if="props.row.tax_active">
                                                {{ parseInt(props.row.tax_rate ? props.row.tax_rate : 0) }}%</span>
                                            <span v-if="!props.row.tax_active">
                                                <b-icon icon="x-lg"/></span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </article>
            </template>
            <template #bottom-left>
                <PerPageSelect v-model="perPage" @change="onPerPageChange" />
            </template>

        </b-table>

        <loader :loading="loading"></loader>
    </div>
</template>
<style scoped>
.employee-name {
    letter-spacing: 1.5px;
    text-decoration: underline;
}

.detail-label {
    font-weight: lighter;
}
</style>
